<template>
  <div id="shared-common">
    <KeepAlive>
      <Suspense>
        <RootComponent />
      </Suspense>
    </KeepAlive>
  </div>

  <ModalContainer ref="modalContainerRef" />
  <LoadingPanel />
</template>

<script setup lang="ts">
import "bootstrap5";
import ModalContainer from "./ModalContainer.vue";
import LoadingPanel from "./LoadingPanel.vue";
import { Component, onErrorCaptured, onMounted, ref } from "vue";
import {
  showUIError,
  ErrorFactory,
  AppError,
  useErrorNotifier,
  useErrorConsumer,
} from "../services/errorHandling";
import { ApiError, HttpError } from "../services/http";
import { useAuthentication } from "../services/auth/authentication/authentication-service-impl";
import RootComponent from "@/components/RootComponent.vue";
import { useRouter } from "../services/routing-services";
import { toastError } from "../services/notification-service";
import { useIntl } from "../services/i18n";
import { setModalContainer } from "../services/errorHandling/error-ui";

const intl = useIntl();
const modalContainerRef = ref<typeof ModalContainer>();
const errorConsumer = useErrorConsumer();
errorConsumer.handleError(handleNotifiedError);

function handleNotifiedError(error: Error) {
  const appError = ErrorFactory.fromError(error);
  const isHandled = appError.handled;

  if (import.meta.env.VITE_IS_DEBUG === "true") {
    if (localStorage.getItem("breakOnError") === "true") {
    }

    if (localStorage.getItem("omitErrorLogging") !== "true") {
      console.error(
        "Error capturado en root-app-component",
        JSON.parse(appError.toJson())
      );
    }
  } else {
    console.error(
      "Error capturado en root-app-component",
      JSON.parse(appError.toJson())
    );
  }

  if (!isHandled) {
    if (findUnauthorizedError(appError)) {
      useAuthentication().clearLoginState();
      useRouter().replace({ name: "home" });
      toastError(intl.i18n.t('shared.errors.ui.session_expired'));
      return true;
    }

    showUIError(appError);
  }

  return false;
}

function findUnauthorizedError(error: AppError): boolean {
  let currentError: any | undefined = error;
  while (currentError != null) {
    if (currentError instanceof ApiError && currentError.innerError instanceof HttpError) {
      if (currentError.innerError.statusCode === 401) {
        return true;
      }
    }
    currentError = currentError?.innerError;
  }
  return false;
}


onErrorCaptured((error: Error) => {
  notifyError(error);
  return false;
});

onMounted(() => {

  if (modalContainerRef.value) {
      setModalContainer(modalContainerRef.value);
  }

  window.onerror = function (message, source, lineno, colno, error?: Error) {

    if (error == null) {
      if (typeof message === "string")
        error = new Error(message);
      else
        error = new Error("Error desconocido");
    }

    notifyError(error);
    return false;
  };

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    let error: Error;
    if (event.reason instanceof Error) {
      error = event.reason;
    } else {
      error = new Error(event.reason);
    }
    notifyError(error);
    return false;
  };
});

function notifyError(error: Error) {
  const appError = ErrorFactory.fromError(error);
  const notifier = useErrorNotifier();
  notifier.notifyError(appError);
}
</script>
